(function (global) {
  'use strict';

  var DEFAULT_API_URL = 'https://discount-verification.silkpartners.workers.dev';
  var CACHE_TTL_MS = 30000;
  var API_TIMEOUT_MS = 8000;
  var COLLECTION_FETCH_TIMEOUT_MS = 5000;

  var verificationCache = {};
  var collectionCache = {};

  function normalizeCode(value) {
    return String(value || '').trim();
  }

  function parseGid(gid) {
    if (gid == null) return null;
    var match = String(gid).match(/\/(\d+)$/);
    return match ? Number(match[1]) : null;
  }

  function themeString(key, fallback) {
    return (global.theme && global.theme.strings && global.theme.strings[key]) || fallback;
  }

  function formatMoneyPlain(cents) {
    var helper = global.DiscountRulesValidation;
    if (helper && typeof helper.formatMoneyPlain === 'function') {
      return helper.formatMoneyPlain(cents);
    }
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  }

  function buildMinAmountMessage(cents, template) {
    var tpl = template || themeString('discountMinAmount',
      'This discount code requires a cart subtotal of [[amount]] or more.');
    return String(tpl).replace(/\[\[amount\]\]/g, formatMoneyPlain(cents));
  }

  function defaultMessages(overrides) {
    overrides = overrides || {};
    return {
      invalidMessage: overrides.invalidMessage || themeString('discountInvalid', 'Invalid discount code'),
      minAmountMessageTemplate: overrides.minAmountMessageTemplate || themeString('discountMinAmount',
        'This discount code requires a cart subtotal of [[amount]] or more.'),
      noEligibleItemsMessage: overrides.noEligibleItemsMessage || themeString('discountNoEligibleItems',
        'This discount code does not apply to any items in your cart.'),
      expiredMessage: overrides.expiredMessage || themeString('discountExpired',
        'This discount code has expired.'),
      notStartedMessage: overrides.notStartedMessage || themeString('discountNotStarted',
        'This discount code is not active yet.'),
      usageLimitMessage: overrides.usageLimitMessage || themeString('discountUsageLimit',
        'This discount code has reached its usage limit.'),
      minQuantityMessage: overrides.minQuantityMessage || null,
      customerLoginRequiredMessage: overrides.customerLoginRequiredMessage || themeString(
        'discountCustomerLoginRequired',
        'Please log in to use this discount code.'
      ),
      shippingCountryInvalidMessage: overrides.shippingCountryInvalidMessage || themeString(
        'discountShippingCountryInvalid',
        'This discount code is not available for your selected country.'
      )
    };
  }

  function resolveLocalizationPayload(options) {
    options = options || {};
    if (options.shipping) return options.shipping;
    var loc = global.theme && global.theme.localization;
    return {
      countryCode: options.countryCode || (loc && loc.countryCode) || null,
      currencyCode: options.currencyCode || (loc && loc.currencyCode) || null
    };
  }

  function mapCartLineForApi(item) {
    if (!item) return null;
    return {
      productId: item.product_id,
      variantId: item.variant_id,
      quantity: Number(item.quantity) || 0,
      lineSubtotal: lineSubtotalCents(item) / 100,
      handle: lineProductHandle(item)
    };
  }

  function buildCartRequestPayload(options) {
    options = options || {};
    var subtotalCents = Number(options.subtotalCents) || 0;
    var items = options.cartItems || [];
    var currencyCode = options.currencyCode;
    if (!currencyCode && global.theme && global.theme.localization) {
      currencyCode = global.theme.localization.currencyCode;
    }
    return {
      subtotal: subtotalCents / 100,
      currencyCode: currencyCode || 'USD',
      itemQuantity: Number(options.itemQuantity) || 0,
      lines: items.map(mapCartLineForApi).filter(Boolean)
    };
  }

  function buildShippingRequestPayload(options) {
    options = options || {};
    var loc = resolveLocalizationPayload(options);
    var shipping = options.shipping || {};
    return {
      countryCode: shipping.countryCode || loc.countryCode || null,
      currencyCode: shipping.currencyCode || loc.currencyCode || null,
      source: shipping.source || 'market',
      shippingPrice: shipping.shippingPrice != null ? shipping.shippingPrice : null
    };
  }

  function buildRequestCacheKey(code, customer, cartPayload, shippingPayload) {
    var subtotalKey = cartPayload && cartPayload.subtotal != null ? String(cartPayload.subtotal) : '0';
    var qtyKey = cartPayload && cartPayload.itemQuantity != null ? String(cartPayload.itemQuantity) : '0';
    var countryKey = shippingPayload && shippingPayload.countryCode ? shippingPayload.countryCode : 'none';
    return normalizeCode(code).toUpperCase() + '|' + customerCacheKey(customer) + '|' +
      subtotalKey + '|' + qtyKey + '|' + countryKey;
  }

  function resolveCustomerPayload(options) {
    if (options && options.customer) return options.customer;
    var themeCustomer = global.theme && global.theme.customer;
    if (themeCustomer && themeCustomer.id) {
      return {
        id: themeCustomer.gid || themeCustomer.id
      };
    }
    return null;
  }

  function customerCacheKey(customer) {
    if (!customer || customer.id == null || customer.id === '') return 'guest';
    var id = String(customer.id);
    var match = id.match(/(\d+)$/);
    return match ? match[1] : id;
  }

  function discountNeedsCustomer(verification) {
    if (!verification) return false;
    var eligibility = verification.customerEligibility || {};
    if (eligibility.needsCustomerInRequest === true) return true;
    var selection = verification.customerSelection || {};
    return selection.mode === 'segments' || selection.mode === 'specific';
  }

  function buildValidationOptions(cart, overrides) {
    overrides = overrides || {};
    var items = overrides.cartItems || (cart && cart.items) || [];
    var itemQuantity = overrides.itemQuantity;
    if (itemQuantity == null) {
      itemQuantity = 0;
      items.forEach(function (item) {
        itemQuantity += Number(item && item.quantity) || 0;
      });
    }

    var messages = defaultMessages(overrides);
    return {
      apiUrl: overrides.apiUrl || DEFAULT_API_URL,
      timeoutMs: overrides.timeoutMs,
      subtotalCents: overrides.subtotalCents != null
        ? Number(overrides.subtotalCents) || 0
        : Number(cart && cart.total_price) || 0,
      itemQuantity: itemQuantity,
      cartItems: items,
      customer: resolveCustomerPayload(overrides),
      invalidMessage: messages.invalidMessage,
      minAmountMessageTemplate: messages.minAmountMessageTemplate,
      noEligibleItemsMessage: messages.noEligibleItemsMessage,
      expiredMessage: messages.expiredMessage,
      notStartedMessage: messages.notStartedMessage,
      usageLimitMessage: messages.usageLimitMessage,
      minQuantityMessage: messages.minQuantityMessage,
      customerLoginRequiredMessage: messages.customerLoginRequiredMessage,
      shippingCountryInvalidMessage: messages.shippingCountryInvalidMessage,
      currencyCode: overrides.currencyCode || (global.theme && global.theme.localization && global.theme.localization.currencyCode) || null,
      countryCode: overrides.countryCode || (global.theme && global.theme.localization && global.theme.localization.countryCode) || null,
      shipping: overrides.shipping || null
    };
  }

  function parseApiResponse(body) {
    if (!body) {
      return { state: 'error', code: 'network' };
    }
    if (body.error === 'not_authorized') {
      return { state: 'error', code: 'service_unavailable', message: body.message };
    }
    if (body.error === 'invalid_code') {
      return { state: 'error', code: 'invalid_request', message: body.message };
    }
    if (Array.isArray(body.errors) && body.errors.length) {
      return { state: 'error', code: 'graphql', message: body.errors[0].message };
    }
    if (body.found === false) {
      return { state: 'not_found', reason: body.reason || 'discount_not_found' };
    }
    if (body.found === true && body.verification) {
      return {
        state: 'ok',
        verification: body.verification,
        capabilities: body.capabilities || null,
        valid: body.valid,
        codeValid: body.codeValid,
        customerOk: body.customerOk,
        cartOk: body.cartOk,
        shippingOk: body.shippingOk,
        customerValidation: body.customerValidation || null,
        cartValidation: body.cartValidation || null,
        shippingValidation: body.shippingValidation || null,
        validationScope: body.validationScope || null
      };
    }
    return { state: 'error', code: 'invalid_response' };
  }

  function idsFromEntities(list) {
    var ids = [];
    (list || []).forEach(function (entity) {
      var id = parseGid(entity && entity.id);
      if (id) ids.push(id);
    });
    return ids;
  }

  function scopeFromApplicableItems(items) {
    if (!items || items.allItems === true) {
      return { allItems: true, variantIds: [], productIds: [], collectionIds: [] };
    }
    var variantIds = idsFromEntities(items.variants);
    var productIds = idsFromEntities(items.products);
    var collectionIds = idsFromEntities(items.collections);
    var restricted = variantIds.length || productIds.length || collectionIds.length;
    return {
      allItems: restricted ? false : items.allItems !== false,
      variantIds: variantIds,
      productIds: productIds,
      collectionIds: collectionIds
    };
  }

  function applicabilityScope(verification) {
    if (!verification) {
      return { allItems: true, variantIds: [], productIds: [], collectionIds: [] };
    }
    if (verification.discountClass === 'bxgy') {
      return scopeFromApplicableItems(verification.buys && verification.buys.applicableItems);
    }
    if (verification.discountClass === 'free_shipping') {
      return { allItems: true, variantIds: [], productIds: [], collectionIds: [] };
    }
    return scopeFromApplicableItems(verification.applicableItems);
  }

  function minimumRequirements(verification) {
    var minSubtotalCents = null;
    var minQuantity = null;
    var req = verification && verification.minimumRequirement;

    if (req && req.type === 'subtotal' && req.amount != null) {
      minSubtotalCents = Math.round(parseFloat(String(req.amount)) * 100);
    }
    if (req && req.type === 'quantity' && req.quantity != null) {
      minQuantity = Number(req.quantity);
    }

    if (verification && verification.discountClass === 'bxgy') {
      var buyValue = verification.buys && verification.buys.value;
      if (buyValue && buyValue.type === 'quantity' && buyValue.quantity != null && minQuantity == null) {
        minQuantity = Number(buyValue.quantity);
      }
      if (buyValue && buyValue.type === 'purchase_amount' && buyValue.amount != null && minSubtotalCents == null) {
        minSubtotalCents = Math.round(parseFloat(String(buyValue.amount)) * 100);
      }
    }

    return {
      minSubtotalCents: isNaN(minSubtotalCents) ? null : minSubtotalCents,
      minQuantity: isNaN(minQuantity) ? null : minQuantity
    };
  }

  function shouldValidateApplicability(capabilities, scope) {
    if (scope.allItems) return false;
    var hasProductScope = !!(scope.variantIds.length || scope.productIds.length);
    var hasCollectionScope = !!scope.collectionIds.length;
    if (!hasProductScope && !hasCollectionScope) return false;
    if (capabilities) {
      if (hasCollectionScope && capabilities.applicableCollections) return true;
      if (hasProductScope && capabilities.applicableProducts) return true;
      if (hasProductScope && hasCollectionScope &&
        (capabilities.applicableProducts || capabilities.applicableCollections)) {
        return true;
      }
    }
    return hasProductScope || hasCollectionScope;
  }

  function lineProductHandle(item) {
    if (!item) return '';
    if (item.handle) return String(item.handle).trim();
    var url = String(item.url || '').trim();
    if (!url) return '';
    var path = url.split('?')[0];
    var parts = path.split('/').filter(Boolean);
    var productsIndex = parts.indexOf('products');
    if (productsIndex >= 0 && parts[productsIndex + 1]) {
      return parts[productsIndex + 1];
    }
    return '';
  }

  function lineSubtotalCents(item) {
    if (!item) return 0;
    if (item.final_line_price != null && item.final_line_price !== '') {
      return Math.round(Number(item.final_line_price));
    }
    if (item.line_price != null && item.line_price !== '') {
      return Math.round(Number(item.line_price));
    }
    var unit = Number(item.final_price != null ? item.final_price : item.price) || 0;
    return Math.round(unit * (Number(item.quantity) || 0));
  }

  function fetchProductCollections(handle) {
    var key = String(handle || '').trim();
    if (!key) return Promise.resolve([]);

    if (Object.prototype.hasOwnProperty.call(collectionCache, key)) {
      return Promise.resolve(collectionCache[key]);
    }

    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = controller ? global.setTimeout(function () {
      try { controller.abort(); } catch (e) {}
    }, COLLECTION_FETCH_TIMEOUT_MS) : null;

    var request = fetch('/products/' + encodeURIComponent(key) + '?view=collections', {
      credentials: 'same-origin',
      signal: controller ? controller.signal : undefined
    })
      .then(function (response) {
        if (!response.ok) throw new Error('collection lookup failed');
        return response.json();
      })
      .then(function (data) {
        var ids = Array.isArray(data && data.collections)
          ? data.collections.map(Number).filter(function (id) { return !isNaN(id); })
          : [];
        collectionCache[key] = ids;
        return ids;
      })
      .catch(function () {
        collectionCache[key] = [];
        return [];
      })
      .finally(function () {
        if (timer) global.clearTimeout(timer);
      });

    collectionCache[key] = request;
    return request;
  }

  function lineMatchesScope(item, scope) {
    if (scope.variantIds.length && scope.variantIds.indexOf(Number(item.variant_id)) >= 0) {
      return true;
    }
    if (scope.productIds.length && scope.productIds.indexOf(Number(item.product_id)) >= 0) {
      return true;
    }
    return false;
  }

  function resolveEligibleCart(scope, cartItems) {
    var items = Array.isArray(cartItems) ? cartItems : [];
    if (scope.allItems) {
      var totalSubtotal = 0;
      var totalQty = 0;
      items.forEach(function (item) {
        totalQty += Number(item.quantity) || 0;
        totalSubtotal += lineSubtotalCents(item);
      });
      return Promise.resolve({
        eligible: items.length > 0,
        eligibleSubtotalCents: totalSubtotal,
        eligibleQuantity: totalQty
      });
    }

    if (!items.length) {
      return Promise.resolve({ eligible: false, eligibleSubtotalCents: 0, eligibleQuantity: 0 });
    }

    var needsCollectionLookup = scope.collectionIds.length > 0;
    var tasks = items.map(function (item) {
      if (lineMatchesScope(item, scope)) {
        return Promise.resolve({ item: item, eligible: true });
      }
      if (!needsCollectionLookup) {
        return Promise.resolve({ item: item, eligible: false });
      }
      var handle = lineProductHandle(item);
      return fetchProductCollections(handle).then(function (productCollectionIds) {
        var eligible = false;
        var i;
        for (i = 0; i < productCollectionIds.length; i++) {
          if (scope.collectionIds.indexOf(Number(productCollectionIds[i])) >= 0) {
            eligible = true;
            break;
          }
        }
        return { item: item, eligible: eligible };
      });
    });

    return Promise.all(tasks).then(function (results) {
      var eligibleSubtotalCents = 0;
      var eligibleQuantity = 0;
      var hasEligible = false;

      results.forEach(function (result) {
        if (!result || !result.eligible) return;
        hasEligible = true;
        eligibleQuantity += Number(result.item.quantity) || 0;
        eligibleSubtotalCents += lineSubtotalCents(result.item);
      });

      return {
        eligible: hasEligible,
        eligibleSubtotalCents: eligibleSubtotalCents,
        eligibleQuantity: eligibleQuantity
      };
    });
  }

  function fail(message) {
    return { ok: false, message: message };
  }

  function pass(verification, capabilities) {
    return { ok: true, verification: verification, capabilities: capabilities };
  }

  function deferToShopify() {
    return { ok: null, fallback: true };
  }

  function checkUsability(verification, messages) {
    if (!verification) {
      return fail(messages.invalidMessage);
    }
    if (verification.isUsable === false || verification.status !== 'ACTIVE') {
      var schedule = verification.schedule || {};
      if (schedule.isWithinSchedule === false) {
        var startsAt = schedule.startsAt ? Date.parse(schedule.startsAt) : NaN;
        if (!isNaN(startsAt) && Date.now() < startsAt) {
          return fail(messages.notStartedMessage);
        }
        return fail(messages.expiredMessage);
      }
      return fail(messages.invalidMessage);
    }
    return null;
  }

  function checkUsage(verification, messages) {
    var usage = verification.usage || {};
    var limit = Number(usage.usageLimit);
    var count = Number(usage.asyncUsageCount);
    if (!isNaN(limit) && limit > 0 && !isNaN(count) && count >= limit) {
      return fail(messages.usageLimitMessage);
    }
    return null;
  }

  function checkMinimums(requirements, cartContext, eligibility, messages) {
    var subtotalCents = Number(cartContext.subtotalCents) || 0;
    var itemQuantity = Number(cartContext.itemQuantity) || 0;

    if (eligibility) {
      subtotalCents = Number(eligibility.eligibleSubtotalCents) || 0;
      itemQuantity = Number(eligibility.eligibleQuantity) || 0;
    }

    if (requirements.minSubtotalCents > 0 && subtotalCents < requirements.minSubtotalCents) {
      return fail(buildMinAmountMessage(requirements.minSubtotalCents, messages.minAmountMessageTemplate));
    }

    if (requirements.minQuantity > 0 && itemQuantity < requirements.minQuantity) {
      var qtyMsg = messages.minQuantityMessage ||
        ('This discount code requires at least ' + requirements.minQuantity + ' eligible items in your cart.');
      return fail(qtyMsg);
    }

    return null;
  }

  function checkCustomerValidation(apiResult, messages, customerProvided) {
    var verification = apiResult.verification;
    var customerValidation = apiResult.customerValidation;
    var needsCustomer = discountNeedsCustomer(verification);

    if (customerValidation && customerValidation.checked === true) {
      if (customerValidation.passed === false) {
        return fail(messages.invalidMessage);
      }
      return null;
    }

    if (customerValidation && customerValidation.reason === 'customer_required') {
      return fail(messages.customerLoginRequiredMessage);
    }

    if (apiResult.valid === false && needsCustomer && !customerProvided) {
      return fail(messages.customerLoginRequiredMessage);
    }

    if (apiResult.valid === false && customerValidation && customerValidation.passed === false) {
      return fail(messages.invalidMessage);
    }

    if (needsCustomer && !customerProvided) {
      return fail(messages.customerLoginRequiredMessage);
    }

    return null;
  }

  function checkBackendCartValidation(apiResult, messages) {
    var cartValidation = apiResult.cartValidation;
    if (!cartValidation || cartValidation.checked !== true) return null;
    if (cartValidation.passed !== false) return null;

    if (cartValidation.reason === 'subtotal_below_minimum' &&
      cartValidation.minimumSubtotal && cartValidation.minimumSubtotal.required != null) {
      var requiredCents = Math.round(parseFloat(String(cartValidation.minimumSubtotal.required)) * 100);
      if (!isNaN(requiredCents) && requiredCents > 0) {
        return fail(buildMinAmountMessage(requiredCents, messages.minAmountMessageTemplate));
      }
    }
    if (cartValidation.reason === 'no_eligible_items') {
      return fail(messages.noEligibleItemsMessage);
    }
    if (cartValidation.reason === 'quantity_below_minimum') {
      return fail(messages.minQuantityMessage || messages.invalidMessage);
    }
    return fail(messages.invalidMessage);
  }

  function checkBackendShippingValidation(apiResult, messages) {
    var shippingValidation = apiResult.shippingValidation;
    if (!shippingValidation || shippingValidation.checked !== true) return null;
    if (shippingValidation.passed !== false) return null;

    if (shippingValidation.reason === 'country_not_allowed') {
      return fail(messages.shippingCountryInvalidMessage || messages.invalidMessage);
    }
    if (shippingValidation.reason === 'shipping_price_exceeds_maximum') {
      return fail(messages.invalidMessage);
    }
    return fail(messages.invalidMessage);
  }

  function resolveEffectiveValid(apiResult) {
    if (apiResult.valid === true) return true;
    if (apiResult.valid !== false) return null;

    var scope = apiResult.validationScope || {};
    var checkoutOnly = Array.isArray(scope.checkoutOnly) ? scope.checkoutOnly : [];
    if (!checkoutOnly.length) return false;

    var shippingValidation = apiResult.shippingValidation || {};
    var customerValidation = apiResult.customerValidation;
    var cartValidation = apiResult.cartValidation;

    var customerOk = apiResult.customerOk === true ||
      !customerValidation || customerValidation.passed !== false;
    var cartOk = apiResult.cartOk === true ||
      !cartValidation || cartValidation.passed !== false;
    var codeOk = apiResult.codeValid !== false;

    if (!codeOk || !customerOk || !cartOk) return false;

    if (shippingValidation.checked !== true) return true;
    if (shippingValidation.passed === true) return true;
    if (shippingValidation.reason === 'shipping_not_provided' ||
      shippingValidation.reason === 'shipping_price_not_provided') {
      return checkoutOnly.some(function (key) {
        return String(key).indexOf('shipping') === 0;
      });
    }

    return false;
  }

  function backendCartValidationActive(apiResult) {
    var cartValidation = apiResult.cartValidation;
    return !!(cartValidation && cartValidation.checked === true && cartValidation.passed !== false);
  }

  function backendHandlesProductEligibility(apiResult) {
    var cartValidation = apiResult.cartValidation;
    return !!(cartValidation && cartValidation.checked === true &&
      (cartValidation.productsChecked === true || cartValidation.eligibleItemsChecked === true));
  }

  function validateAgainstCart(apiResult, cartContext, messageOverrides) {
    var messages = defaultMessages(messageOverrides);
    var customerProvided = !!(messageOverrides && messageOverrides.customerProvided);

    if (apiResult.state === 'error') {
      if (apiResult.code === 'service_unavailable' || apiResult.code === 'network') {
        return deferToShopify();
      }
      return fail(apiResult.message || messages.invalidMessage);
    }

    if (apiResult.state === 'not_found') {
      return fail(messages.invalidMessage);
    }

    var verification = apiResult.verification;
    var capabilities = apiResult.capabilities;
    var usabilityError = checkUsability(verification, messages);
    if (usabilityError) return usabilityError;

    var customerError = checkCustomerValidation(apiResult, messages, customerProvided);
    if (customerError) return customerError;

    var backendCartError = checkBackendCartValidation(apiResult, messages);
    if (backendCartError) return backendCartError;

    var backendShippingError = checkBackendShippingValidation(apiResult, messages);
    if (backendShippingError) return backendShippingError;

    var effectiveValid = resolveEffectiveValid(apiResult);
    if (effectiveValid === false) {
      return fail(messages.invalidMessage);
    }

    if (apiResult.valid === false && effectiveValid !== true) {
      return fail(messages.invalidMessage);
    }

    var usageError = checkUsage(verification, messages);
    if (usageError) return usageError;

    var scope = applicabilityScope(verification);
    var requirements = minimumRequirements(verification);
    var cartItems = cartContext.cartItems || [];
    var skipLocalMinimum = backendCartValidationActive(apiResult);
    var skipLocalProducts = backendHandlesProductEligibility(apiResult);

    if (!skipLocalProducts && !shouldValidateApplicability(capabilities, scope)) {
      if (skipLocalMinimum) {
        return pass(verification, capabilities);
      }
      var minimumErrorNoScope = checkMinimums(requirements, cartContext, null, messages);
      return minimumErrorNoScope || pass(verification, capabilities);
    }

    if (skipLocalProducts) {
      return pass(verification, capabilities);
    }

    return resolveEligibleCart(scope, cartItems).then(function (eligibility) {
      if (!eligibility.eligible) {
        return fail(messages.noEligibleItemsMessage);
      }
      if (skipLocalMinimum) {
        return pass(verification, capabilities);
      }
      var minimumError = checkMinimums(requirements, cartContext, eligibility, messages);
      return minimumError || pass(verification, capabilities);
    });
  }

  function fetchVerification(code, apiUrl, timeoutMs, customer, cartPayload, shippingPayload) {
    var cacheKey = buildRequestCacheKey(code, customer, cartPayload, shippingPayload);
    var cached = verificationCache[cacheKey];
    if (cached && (Date.now() - cached.ts) < CACHE_TTL_MS) {
      return Promise.resolve(cached.result);
    }

    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = controller ? global.setTimeout(function () {
      try { controller.abort(); } catch (e) {}
    }, timeoutMs) : null;

    var requestBody = { code: normalizeCode(code) };
    if (customer && customer.id != null && customer.id !== '') {
      requestBody.customer = { id: customer.id };
    }
    if (cartPayload) {
      requestBody.cart = cartPayload;
    }
    if (shippingPayload) {
      requestBody.shipping = shippingPayload;
    }

    return fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
      signal: controller ? controller.signal : undefined
    })
      .then(function (response) {
        return response.json().then(function (body) {
          return { status: response.status, body: body };
        });
      })
      .then(function (result) {
        if (result.status === 503 && result.body && result.body.error === 'not_authorized') {
          return parseApiResponse(result.body);
        }
        if (!result.status || result.status >= 500) {
          return { state: 'error', code: 'service_unavailable' };
        }
        return parseApiResponse(result.body);
      })
      .catch(function () {
        return { state: 'error', code: 'network' };
      })
      .finally(function () {
        if (timer) global.clearTimeout(timer);
      })
      .then(function (apiResult) {
        if (apiResult.state === 'ok') {
          verificationCache[cacheKey] = { result: apiResult, ts: Date.now() };
        }
        return apiResult;
      });
  }

  function verify(code, options) {
    options = options || {};
    var trimmed = normalizeCode(code);
    if (!trimmed) {
      return Promise.resolve(fail(options.invalidMessage || themeString('discountInvalid', 'Invalid discount code')));
    }

    var apiUrl = options.apiUrl || DEFAULT_API_URL;
    var timeoutMs = options.timeoutMs == null ? API_TIMEOUT_MS : Number(options.timeoutMs);
    var customer = resolveCustomerPayload(options);
    var cartPayload = buildCartRequestPayload(options);
    var shippingPayload = buildShippingRequestPayload(options);
    var cartContext = {
      subtotalCents: Number(options.subtotalCents) || 0,
      itemQuantity: Number(options.itemQuantity) || 0,
      cartItems: options.cartItems || []
    };
    var messageOverrides = {};
    Object.keys(options).forEach(function (key) {
      messageOverrides[key] = options[key];
    });
    messageOverrides.customerProvided = !!(customer && customer.id != null && customer.id !== '');

    return fetchVerification(trimmed, apiUrl, timeoutMs, customer, cartPayload, shippingPayload).then(function (apiResult) {
      return validateAgainstCart(apiResult, cartContext, messageOverrides);
    });
  }

  global.DiscountVerification = {
    verify: verify,
    parseApiResponse: parseApiResponse,
    validateAgainstCart: validateAgainstCart,
    buildValidationOptions: buildValidationOptions,
    buildCartRequestPayload: buildCartRequestPayload,
    buildShippingRequestPayload: buildShippingRequestPayload,
    resolveCustomerPayload: resolveCustomerPayload,
    clearCache: function () {
      verificationCache = {};
      collectionCache = {};
    },
    DEFAULT_API_URL: DEFAULT_API_URL
  };
})(window);
